import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Maximize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

export default function Lightbox({ artwork, onClose }) {
  const [fullImage, setFullImage] = useState(null);

  // When the lightbox opens on an artwork whose image_data is empty
  // (list endpoint strips it for payload speed), fetch the full record.
  useEffect(() => {
    if (!artwork) {
      setFullImage(null);
      return;
    }
    if (artwork.image_data) {
      setFullImage(artwork.image_data);
      return;
    }
    let cancelled = false;
    setFullImage(null);
    api
      .get(`/artworks/${artwork.id}`)
      .then((r) => {
        if (!cancelled) setFullImage(r.data.image_data || r.data.thumb_data || "");
      })
      .catch(() => {
        if (!cancelled) setFullImage(artwork.thumb_data || "");
      });
    return () => {
      cancelled = true;
    };
  }, [artwork]);

  // Close on Escape
  useEffect(() => {
    if (!artwork) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [artwork, onClose]);

  const displaySrc = fullImage || artwork?.thumb_data || "";

  const content = (
    <AnimatePresence>
      {artwork && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex flex-col"
          data-testid="lightbox-overlay"
          onClick={onClose}
        >
          {/* Top bar */}
          <div
            className="flex items-center justify-between px-6 sm:px-10 py-5 border-b border-white/5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0">
              <p className="plaque-label">Now Viewing</p>
              <h2 className="font-serif-display text-xl sm:text-2xl mt-1 truncate" data-testid="lightbox-title">
                {artwork.title}
                {artwork.year && (
                  <span className="text-zinc-500 italic font-light ml-3">{artwork.year}</span>
                )}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to={`/artwork/${artwork.id}`}
                onClick={onClose}
                className="hidden sm:inline-block text-xs tracking-[0.3em] uppercase border-b border-[#D4AF37] pb-1 text-[#D4AF37] hover:text-white hover:border-white transition-colors"
                data-testid="lightbox-details-link"
              >
                View curator card →
              </Link>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-white p-2 transition-colors"
                aria-label="Close"
                data-testid="lightbox-close-button"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Image stage */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex-1 flex items-center justify-center p-4 sm:p-10 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={displaySrc}
              alt={artwork.title}
              className="max-w-full max-h-full object-contain shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
              data-testid="lightbox-image"
              style={{
                boxShadow:
                  "0 0 0 1px rgba(255,255,255,0.05), 0 0 0 8px #141414, 0 0 0 9px rgba(212, 175, 55, 0.18), 0 40px 80px -30px rgba(0,0,0,0.9)",
              }}
            />
          </motion.div>

          {/* Bottom plaque */}
          <div
            className="px-6 sm:px-10 py-5 border-t border-white/5 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {artwork.medium && (
              <span className="text-zinc-400">
                <span className="plaque-label mr-2">Medium</span>
                <em className="font-serif-display not-italic text-zinc-200">{artwork.medium}</em>
              </span>
            )}
            {artwork.dimensions && (
              <span className="text-zinc-400">
                <span className="plaque-label mr-2">Size</span>
                <span className="text-zinc-200">{artwork.dimensions}</span>
              </span>
            )}
            {artwork.availability && (
              <span className="text-[#D4AF37]">
                <span className="plaque-label mr-2 text-zinc-500">Status</span>
                <span className="capitalize">{artwork.availability}</span>
              </span>
            )}
            <span className="ml-auto plaque-label hidden sm:flex items-center gap-2">
              <Maximize2 className="w-3 h-3" /> Click image to close · ESC
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
